import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion, useInView, useReducedMotion } from 'motion/react'
import { Check, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InteractiveDots } from '@/components/motion'
import { OfficeAppIcon } from '@/components/landing/OfficeAppIcon'
import { OUT_EASE } from '@/components/landing/service/halftone'
import type { PlatformFeature } from '@/data/platform'

/** What the task pane does to the document in each scene (illustrative text). */
const SCENES = [
  {
    doc: 'Proposal',
    action: 'Polish',
    before: 'We think our team could maybe help you improve your onboarding process at some point.',
    after: 'Our team will streamline your onboarding in three clear steps.',
    checks: ['Tone: confident', 'Template: Proposal', 'Ready to send'],
  },
  {
    doc: 'Brand review',
    action: 'Brand check',
    before: 'Quarterly results, set in an off-brand font and colour.',
    after: 'Quarterly results, in the approved font and colour.',
    checks: ['Fonts match the brand', 'Logo is current', 'Colours approved'],
  },
  {
    doc: 'Leadership update',
    action: 'Polish',
    before: 'So basically it was a pretty good quarter overall, all things considered really.',
    after: 'A strong quarter, and a clear plan for the next one.',
    checks: ['Shorter, same meaning', 'Tone: executive', 'Ready to share'],
  },
] as const

const SCENE_MS = 5200
const STEP_MS = 1300

/**
 * Full-width 16:9 media block (the about page's video slot): the use cases on
 * the left, a Word window on the right where the Templatean task pane polishes
 * the document, scene by scene. Pauses off-screen; static (polished first
 * scene) under reduced motion.
 */
export function WordPolishMedia({ feature, headingId }: { feature: PlatformFeature; headingId: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { amount: 0.35 })
  const reduce = useReducedMotion()
  const [scene, setScene] = useState(0)
  const [phase, setPhase] = useState(reduce ? 3 : 0)

  useEffect(() => {
    if (reduce) {
      setPhase(3)
      return
    }
    if (!inView) return
    const id = window.setTimeout(() => {
      if (phase < 3) setPhase(phase + 1)
      else {
        setScene((s) => (s + 1) % SCENES.length)
        setPhase(0)
      }
    }, phase < 3 ? STEP_MS : SCENE_MS - 3 * STEP_MS)
    return () => window.clearTimeout(id)
  }, [inView, reduce, phase])

  const s = SCENES[scene % SCENES.length]!
  const polished = phase >= 2

  return (
    <section aria-labelledby={headingId} className="landing-container">
      <div ref={ref} className="relative overflow-hidden bg-[var(--color-landing-secondary)] xl:aspect-video">
        <InteractiveDots />
        <div className="relative z-10 grid h-full gap-8 p-5 sm:p-8 lg:grid-cols-[minmax(0,0.34fr)_minmax(0,1fr)] lg:items-center lg:gap-12 lg:p-[4%]">
          {/* Use cases; the one on screen is in full white */}
          <div className="order-2 lg:order-1">
            <h2 id={headingId} className="font-[family-name:var(--font-landing-display)] text-[1.6rem] uppercase leading-[1.4] tracking-[-0.02em] text-white">
              One pane, every team.
            </h2>
            <ol className="mt-8 grid gap-6 border-t border-white/25 pt-6">
              {feature.useCases.map((u, i) => (
                <li
                  key={u.title}
                  aria-current={i === scene ? 'step' : undefined}
                  className={cn('transition-opacity duration-500', i === scene ? 'opacity-100' : 'opacity-60')}
                >
                  <div className="flex items-center gap-3">
                    <span aria-hidden className={cn('h-2.5 w-2.5 shrink-0 transition-colors duration-300', i === scene ? 'bg-[#ff00f2]' : 'bg-white/50')} />
                    <h3 className="font-[family-name:var(--font-landing-subdisplay)] text-lg font-bold uppercase tracking-[-0.01em] text-white">{u.title}</h3>
                    <span className="ml-auto text-xs font-bold uppercase tracking-wide text-white/75">{u.industry}</span>
                  </div>
                  <p className="landing-text-body-sm mt-2 pl-[1.375rem] text-white/85">{u.description}</p>
                </li>
              ))}
            </ol>
          </div>

          {/* Word window with the Templatean task pane */}
          <div aria-hidden className="order-1 bg-white text-[var(--color-landing-gray-900)] shadow-[0_40px_80px_-30px_rgb(40_0_80_/_0.7)] [container-type:inline-size] lg:order-2">
            <div className="flex items-center gap-2 border-b border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] px-3 py-2">
              <OfficeAppIcon app="word" className="h-6 w-6" />
              <span className="text-sm font-semibold">{s.doc}</span>
              <span className="ml-auto hidden gap-4 text-xs text-[var(--color-landing-gray-500)] sm:flex">
                <span>Home</span>
                <span>Insert</span>
                <span className="font-semibold text-[var(--color-landing-accent)]">Templatean</span>
              </span>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)] sm:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
              {/* Document page */}
              <div className="min-h-[16rem] border-r border-[var(--color-landing-gray-200)] bg-[var(--color-landing-gray-50)] p-[3cqw] sm:min-h-0">
                <div className="bg-white p-[4cqw] shadow-[0_6px_18px_-10px_rgb(0_0_0_/_0.25)]">
                  <div className="h-[1.6cqw] min-h-2 w-[55%] bg-[var(--color-landing-gray-900)]" />
                  <div className="mt-[1.4cqw] h-[0.9cqw] min-h-1 w-[30%] bg-[var(--color-landing-accent)]" />
                  {[96, 90, 94].map((w, i) => (
                    <div key={i} className="mt-[1.4cqw] h-[0.8cqw] min-h-1 bg-[var(--color-landing-gray-200)]" style={{ width: `${w}%` }} />
                  ))}
                  {/* The sentence being polished */}
                  <div className="relative mt-[2cqw] min-h-[4.5em] text-[clamp(11px,1.55cqw,17px)] leading-snug">
                    <motion.span
                      className="absolute -inset-[0.3em] origin-left bg-[#ff00f2]/15"
                      initial={false}
                      animate={{ scaleX: phase === 1 ? 1 : 0, opacity: phase === 1 ? 1 : 0 }}
                      transition={{ duration: 0.6, ease: OUT_EASE }}
                    />
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.p
                        key={`${scene}-${polished ? 'after' : 'before'}`}
                        className={cn(
                          'relative',
                          !polished && scene === 1 && 'font-serif italic text-[#8a6d00]',
                          polished && 'font-medium'
                        )}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.35, ease: OUT_EASE }}
                      >
                        {polished ? s.after : s.before}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                  {[92, 98, 70].map((w, i) => (
                    <div key={i} className="mt-[1.4cqw] h-[0.8cqw] min-h-1 bg-[var(--color-landing-gray-200)]" style={{ width: `${w}%` }} />
                  ))}
                </div>
              </div>

              {/* Task pane */}
              <div className="hidden flex-col gap-[1.6cqw] p-[2.4cqw] text-[clamp(10px,1.3cqw,15px)] sm:flex">
                <span className="font-bold tracking-[-0.01em]">
                  templ<span className="text-[var(--color-landing-accent)]">a</span>tean
                </span>
                <div className="flex gap-[0.5em]">
                  {['Polish', 'Brand check', 'Insert'].map((a) => (
                    <span
                      key={a}
                      className={cn(
                        'px-[0.6em] py-[0.3em] text-[0.85em] font-semibold uppercase',
                        a === s.action ? 'bg-[var(--color-landing-accent)] text-white' : 'bg-[var(--color-landing-gray-100)] text-[var(--color-landing-gray-600)]'
                      )}
                    >
                      {a}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-[0.5em] bg-[var(--color-landing-gray-50)] p-[0.7em]">
                  <Sparkles className="h-[1.1em] w-[1.1em] shrink-0 text-[var(--color-landing-accent)]" />
                  <span>{phase === 0 ? 'Reading the document…' : phase === 1 ? 'Rewriting the selection…' : 'Done. Review and accept.'}</span>
                </div>
                <ul className="grid gap-[0.7em]">
                  {s.checks.map((c, i) => (
                    <li key={`${scene}-${c}`} className="flex items-center gap-[0.6em]">
                      <motion.span
                        className="flex h-[1.4em] w-[1.4em] shrink-0 items-center justify-center bg-[#00d722] text-white"
                        initial={false}
                        animate={{ opacity: phase >= 2 ? 1 : 0.15, scale: phase >= 2 ? 1 : 0.7 }}
                        transition={{ duration: 0.35, ease: OUT_EASE, delay: phase >= 2 ? i * 0.15 : 0 }}
                      >
                        <Check className="h-[0.9em] w-[0.9em]" strokeWidth={3} />
                      </motion.span>
                      {c}
                    </li>
                  ))}
                </ul>
                <span className={cn('mt-auto py-[0.6em] text-center text-[0.85em] font-bold uppercase text-white transition-colors duration-300', phase >= 3 ? 'bg-[#00b81d]' : 'bg-[var(--color-landing-primary)]')}>
                  {phase >= 3 ? 'Accepted' : 'Accept'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
